import { useEffect, useMemo, useState } from "react";
import { Check, Loader2, Tag, X } from "lucide-react";
import { toast } from "react-toastify";
import { Button } from "@/components/atoms/Button";
import { SearchInput } from "@/components/molecules/SearchInput";
import { labelsService, type LabelSummary } from "@/services/labelsService";
import { cn } from "@/lib/cn";
import { getApiErrorMessage } from "@/utils/apiErrors";

interface Props {
  open: boolean;
  contactIds: string[];
  /** Same labels as the Contacts "Any label" filter — listed for this page's scope. */
  labels: LabelSummary[];
  loadingLabels?: boolean;
  onClose: () => void;
  /** Fired after labels are saved, so the table can show the new mapping. */
  onApplied: () => void;
}

/** Whether the ticked labels are granted to the contacts or taken off them. */
type LabelMode = "add" | "remove";

/**
 * Bulk-assigns existing labels to the contacts ticked on the list, or takes them
 * off again — the Add/Remove switch drives the same picker and Save button.
 * Membership is changed via the same `PATCH /labels/{id}` the Labels page uses;
 * contacts other than the ticked ones keep their place on each label.
 */
export function ContactApplyLabelDialog({
  open,
  contactIds,
  labels,
  loadingLabels = false,
  onClose,
  onApplied,
}: Props) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // The same picker serves both directions: ticked labels are either granted to
  // the contacts or taken off them, so an operator undoing a mislabel does not
  // have to go to the Labels page and edit membership by hand.
  const [mode, setMode] = useState<LabelMode>("add");

  useEffect(() => {
    if (!open) return;
    setSearch("");
    setSelectedIds([]);
    setMode("add");
    setError(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !saving) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, saving]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return labels;
    return labels.filter(
      (label) =>
        label.name?.toLowerCase().includes(q) ||
        (label.labelDescription ?? "").toLowerCase().includes(q),
    );
  }, [labels, search]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const allVisibleSelected =
    visible.length > 0 && visible.every((label) => selectedSet.has(String(label.id)));

  if (!open) return null;

  const toggle = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleVisible = () => {
    const ids = visible.map((label) => String(label.id));
    setSelectedIds((prev) => {
      if (allVisibleSelected) return prev.filter((id) => !ids.includes(id));
      return [...new Set([...prev, ...ids])];
    });
  };

  const removing = mode === "remove";

  const save = async () => {
    const picked = labels.filter((label) => selectedSet.has(String(label.id)));
    if (picked.length === 0 || contactIds.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      await Promise.all(
        picked.map((label) =>
          removing
            ? labelsService.removeContacts(label, contactIds)
            : labelsService.addContacts(label, contactIds),
        ),
      );
      const labelCount = `${picked.length} label${picked.length === 1 ? "" : "s"}`;
      const contactCount = `${contactIds.length} contact${contactIds.length === 1 ? "" : "s"}`;
      toast.success(
        removing
          ? `Removed ${labelCount} from ${contactCount}.`
          : `Applied ${labelCount} to ${contactCount}.`,
      );
      onApplied();
      onClose();
    } catch (e: unknown) {
      setError(
        getApiErrorMessage(e) ??
          `Failed to ${removing ? "remove" : "apply"} labels. Please try again.`,
      );
    } finally {
      setSaving(false);
    }
  };

  const target = contactIds.length === 1 ? "this contact" : `${contactIds.length} contacts`;
  const heading = removing ? `Remove labels from ${target}` : `Apply labels to ${target}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm"
        onClick={() => !saving && onClose()}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="apply-label-title"
        className="relative mx-4 flex max-h-[85vh] w-full max-w-md flex-col overflow-hidden rounded-lg bg-white shadow-lg"
      >
        <div className="flex items-start justify-between gap-4 border-b border-bridge-border/60 px-6 py-4">
          <div className="flex min-w-0 items-start gap-3">
            <span className="mt-0.5 shrink-0 rounded-full bg-bridge-accent-tint p-2 text-bridge-accent">
              <Tag size={18} />
            </span>
            <div className="min-w-0">
              <h2 id="apply-label-title" className="truncate text-lg font-bold text-bridge-fg">
                {heading}
              </h2>
              <p className="mt-0.5 text-xs text-bridge-muted">
                {removing
                  ? "Tick the labels to take off. Other contacts on a label stay on it."
                  : "Tick the labels to add. Contacts already on a label stay there."}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="shrink-0 rounded-lg p-1.5 text-bridge-muted transition-colors hover:text-bridge-fg disabled:opacity-40"
          >
            <X size={18} />
          </button>
        </div>

        <div className="border-b border-bridge-border/60 px-6 py-3 space-y-2">
          <div className="inline-flex rounded-lg border border-bridge-border p-0.5">
            {(["add", "remove"] as const).map((option) => (
              <button
                key={option}
                type="button"
                disabled={saving}
                onClick={() => setMode(option)}
                className={cn(
                  "rounded-md px-3 py-1 text-xs font-medium transition-colors disabled:opacity-40",
                  mode === option
                    ? option === "remove"
                      ? "bg-bridge-danger text-white"
                      : "bg-bridge-accent text-white"
                    : "text-bridge-muted hover:text-bridge-fg",
                )}
              >
                {option === "add" ? "Add labels" : "Remove labels"}
              </button>
            ))}
          </div>
          <SearchInput value={search} onChange={setSearch} placeholder="Search labels..." autoFocus />
          {!loadingLabels && visible.length > 0 && (
            <label className="flex items-center gap-2 text-sm font-medium text-slate-800 cursor-pointer">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-bridge-border accent-bridge-accent"
                checked={allVisibleSelected}
                ref={(el) => {
                  if (el) el.indeterminate = selectedIds.length > 0 && !allVisibleSelected;
                }}
                onChange={toggleVisible}
              />
              Select all
              <span className="font-normal text-bridge-muted">
                ({selectedIds.length} of {labels.length} selected)
              </span>
            </label>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-2">
          {loadingLabels && (
            <div className="flex items-center gap-2 py-8 justify-center text-sm text-bridge-muted">
              <Loader2 size={16} className="animate-spin" />
              Loading labels…
            </div>
          )}

          {error && (
            <div className="mx-2 my-2 rounded-lg border border-bridge-danger/30 bg-red-50 px-3 py-2 text-sm text-bridge-danger">
              {error}
            </div>
          )}

          {!loadingLabels && !error && visible.length === 0 && (
            <p className="px-2 py-8 text-center text-sm text-bridge-muted">
              {search.trim() ? "No labels match that search." : "No labels in this account."}
            </p>
          )}

          {!loadingLabels &&
            visible.map((label) => {
              const id = String(label.id);
              const checked = selectedSet.has(id);
              return (
                <label
                  key={id}
                  className={cn(
                    "flex cursor-pointer items-start gap-2.5 rounded-lg px-2 py-2 hover:bg-slate-50",
                    checked && (removing ? "bg-bridge-danger/10" : "bg-bridge-accent/10"),
                  )}
                >
                  <span
                    className={cn(
                      "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border",
                      checked
                        ? removing
                          ? "border-bridge-danger bg-bridge-danger text-white"
                          : "border-bridge-accent bg-bridge-accent text-white"
                        : "border-bridge-border bg-white",
                    )}
                  >
                    {checked && <Check size={12} strokeWidth={3} />}
                  </span>
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={checked}
                    onChange={() => toggle(id)}
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-slate-800">{label.name}</span>
                    {label.labelDescription?.trim() && (
                      <span className="block text-xs text-bridge-muted truncate">
                        {label.labelDescription}
                      </span>
                    )}
                  </span>
                </label>
              );
            })}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-bridge-border/60 bg-gray-50 px-6 py-4">
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant={removing ? "danger" : "primary"}
            size="sm"
            onClick={() => void save()}
            loading={saving}
            disabled={loadingLabels || selectedIds.length === 0 || contactIds.length === 0}
          >
            {removing ? "Remove" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}
